// Slim top bar. Owns the logo, the greeting, and the burger button —
// the burger now toggles a left-side SideNav rather than the right-drawer
// pattern this file used to render. Drawer logic, login/logout, and
// language toggle have moved into SideNav so this component stays a
// dumb top strip.
import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { useTranslation } from "../i18n";
import { getGreeting } from "./helpers/greeting";
import { isStagingHost } from "./helpers/environment";
import { useSideNavContext } from "./SideNavContext";
import { isManagerPlus } from "../access/positions";
import { useAccess } from "../access/AccessContext";
import AdminSearch from "./admin-search";
import jliLogo from "../images/jli-logo-white.svg";
import "../styles/header.css";

const Header = () => {
    const { t, language } = useTranslation();
    const location = useLocation();
    const { collapsed, drawerOpen, isDesktop, scanMode, setCollapsed, setDrawerOpen } = useSideNavContext();

    const userData = window.localStorage.getItem("user");
    const userObject = userData !== null ? JSON.parse(userData) : { name: "", permissions: "" };
    const isLoggedIn = window.localStorage.getItem("isLoggedIn") === "true";
    // Same rule as SideNav: the `admin` area key once the access context is
    // authoritative, the Manager+ rank until then.
    const { authoritative, hasAccess, pages } = useAccess();
    const hasAdminPage = Object.keys(pages ?? {}).some(k => k.startsWith("admin."));
    const isAdmin = authoritative ? hasAccess("admin") && hasAdminPage : isManagerPlus(userObject.position ?? userObject.permissions);
    const isAdminRoute = location.pathname.startsWith("/admin");
    const firstName = userObject.name ? userObject.name.split(" ")[0] : "";
    const greeting = useMemo(
        () => firstName ? getGreeting(firstName, language) : "",
        [firstName, language],
    );

    // Burger has two roles: on admin desktop the SideNav is persistent,
    // so it just collapses/expands. Everywhere else (including scan
    // pages, even for admins on desktop) it opens/closes the overlay
    // drawer. Both states are mutually exclusive at any moment.
    const persistent = isAdmin && isDesktop && !scanMode;
    const expanded = persistent ? !collapsed : drawerOpen;
    const onBurger = () => {
        if (persistent) setCollapsed(!collapsed);
        else setDrawerOpen(!drawerOpen);
    };

    const burgerLabelKey = persistent
        ? (collapsed ? "sidenav_expand" : "sidenav_collapse")
        : (drawerOpen ? "sidenav_close" : "sidenav_open");

    return (
        <header>
            <div className="header-left">
                <button
                    className="header-burger"
                    onClick={onBurger}
                    aria-label={t(burgerLabelKey)}
                    aria-expanded={expanded}
                    aria-controls="primary-navigation"
                >
                    <Menu size={28} />
                </button>
                <Link to="/" className="header-logo-link" aria-label="JLI Trading">
                    <img className="header-logo" src={jliLogo} alt="JLI Trading" />
                </Link>
                {/* Environment marker — only ever renders on the staging
                    host (runtime hostname check, nothing build-time), so
                    the same code merged to main shows nothing in
                    production. */}
                {isStagingHost() && (
                    <span className="header-env-pill">{t("header_env_staging")}</span>
                )}
            </div>
            {/* Global admin destination search — only rendered for admins
                inside /admin/*. It's the primary cross-page navigator
                now that individual admin pages no longer carry their
                own search input. */}
            {isAdmin && isAdminRoute && (
                <div className="header-search">
                    <AdminSearch className="admin-search--dark" />
                </div>
            )}
            <div className="header-right">
                {isLoggedIn && greeting && (
                    <span className="header-username">{greeting}</span>
                )}
            </div>
        </header>
    );
};

export default Header;
